import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getCurrentBookingSlots } from "../../State/BookingSlot/Action";
import {
  MenuItem,
  Select,
  Button,
  Box,
  Grid,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import moment from "moment";

const DateTimeButton = ({ setDateTime, delay }) => {
  const dispatch = useDispatch();
  const bookingSlot = useSelector((store) => store.bookingSlot);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTime, setSelectedTime] = useState(null);
  const [slotsByDate, setSlotsByDate] = useState({});
  const [alertOpen, setAlertOpen] = useState(false);

  useEffect(() => {
    dispatch(getCurrentBookingSlots());
  }, [dispatch]);

  useEffect(() => {
    if (bookingSlot?.slots) {
      const groupedSlots = bookingSlot.slots.reduce((acc, slot) => {
        const dateKey = moment(slot.date).format("YYYY-MM-DD");
        if (!acc[dateKey]) acc[dateKey] = [];
        acc[dateKey].push(slot);
        return acc;
      }, {});

      setSlotsByDate(groupedSlots);

      const today = moment().format("YYYY-MM-DD");
      const currentTimeInMinutes = getCurrentTimeInMinutes();
      const isPast5PM = currentTimeInMinutes >= 17 * 60;

      let autoSelectedDate = isPast5PM
        ? Object.keys(groupedSlots).find((date) => date > today) || today
        : today;

      setSelectedDate(autoSelectedDate);
    }
  }, [bookingSlot.slots]);

  const getCurrentTimeInMinutes = () =>
    moment().hours() * 60 + moment().minutes();

  const formatTime = (totalMinutes) => {
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return new Date(0, 0, 0, hours, minutes)
      .toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .replace(" ", "");
  };

  const updateDateTime = (date, time) => {
    if (!slotsByDate[date]) return;
    setSelectedTime(time);
    const formattedDateTime = moment(
      `${date} ${formatTime(time)}`,
      "YYYY-MM-DD h:mm A"
    ).format("YYYY-MM-DD HH:mm:ss.SSS");
    setDateTime(formattedDateTime);
  };

  return (
    <Box p={2}>
      <InputLabel id="date-label">Date</InputLabel>
      <Select
        labelId="date-label"
        value={selectedDate}
        onChange={(e) => setSelectedDate(e.target.value)}
        fullWidth
        sx={{ mb: 2 }}
      >
        {Object.keys(slotsByDate).map((date) => (
          <MenuItem key={date} value={date}>
            {moment(date).format("MMMM Do, YYYY")}
          </MenuItem>
        ))}
      </Select>

      <Grid container spacing={1}>
        {slotsByDate[selectedDate]?.map((slot) => {
          const currentTimeInMinutes = getCurrentTimeInMinutes() + delay;
          const today = moment().format("YYYY-MM-DD");
          const isCurrentDate = selectedDate === today;
          const isDisabledDueToDelay =
            isCurrentDate && slot.time < currentTimeInMinutes;
          const isBooked = slot.booked;
          const isSelected = selectedTime === slot.time;

          return (
            <Grid item xs={4} sm={4} md={2.4} key={slot.id || slot.time}>
              <Box
                onClick={() => {
                  if (isBooked || isDisabledDueToDelay) {
                    setAlertOpen(true);
                  } else {
                    updateDateTime(selectedDate, slot.time);
                  }
                }}
                sx={{
                  cursor:
                    isBooked || isDisabledDueToDelay
                      ? "not-allowed"
                      : "pointer",
                }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    bgcolor:
                      isBooked || isDisabledDueToDelay
                        ? "gray"
                        : isSelected
                        ? "#1565C0"
                        : "green",
                    color: "white",
                    "&:hover": {
                      bgcolor:
                        isBooked || isDisabledDueToDelay ? "gray" : "#0D47A1",
                    },
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    px: 2,
                    opacity: isDisabledDueToDelay ? 0.5 : 1,
                    whiteSpace: "nowrap",
                    fontSize: isSelected ? "1.1rem" : "1rem",
                    fontWeight: isSelected ? "bold" : "normal",
                    transform: isSelected ? "scale(1.1)" : "scale(1)",
                    transition: "transform 0.2s ease-in-out",
                  }}
                  disabled={isBooked || isDisabledDueToDelay}
                >
                  {formatTime(slot.time)}
                </Button>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      <Dialog open={alertOpen} onClose={() => setAlertOpen(false)}>
        <DialogTitle>Alert</DialogTitle>
        <DialogContent>This slot is not available</DialogContent>
        <DialogActions>
          <Button onClick={() => setAlertOpen(false)} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DateTimeButton;
